import {isUndefined} from 'lodash';
import React, {FC} from 'react';
import styled, {DefaultTheme, useTheme} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {UnknownAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/unknownAvatar';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {isVisualRecipient} from '../../../../../helpers/modelHelpers';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {TeammateOnlineStatusesEnum} from '../../../../../store/data/teammates/onlineTeammatesSelectors';
import {ContactCardLocationsEnum} from '../../../../../store/panels/contacts/contactCard/contactCardPanelState';
import {AvatarBadgeStatus} from '../../../../common/avatars/avatarBadgeStatus';
import {ContactAvatar} from '../../../../common/avatars/contactAvatar';
import {ContactCardWrapperContainer} from '../../../../contactCard/contactCardWrapperContainer';

/*
 * Props.
 */

interface MessageViewerAvatarProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | AccountDetailsPanelId | ContactDetailsPanelId;
  message: MessageModel;
  author: TeammateModel | VisualRecipientModel | undefined;
  isChatAvatar?: boolean;
  isChatUserOnline?: boolean;
  className?: string;
}

/*
 * Constants.
 */

const avatarSize = VisualSizesEnum.LARGE;

/*
 * Style.
 */

const StyledAvatarDiv = styled.div`
  position: relative;
`;

const StyledAvatarWrapper = styled.div`
  margin: 8px 7px;
  position: relative;
`;

/*
 * Component.
 */

const onlineLabel = <Trans id="contact-avatar-chat-online-tooltip">Online</Trans>;

export const MessageViewerAvatar: FC<MessageViewerAvatarProps> = (props) => {
  const {panelId, isChatAvatar, isChatUserOnline, author} = props;
  const theme = useTheme();

  if (!isVisualRecipient(author)) {
    return (
      <StyledAvatarWrapper className={props.className}>
        <StyledAvatarDiv>{renderAvatar(props, theme)}</StyledAvatarDiv>
      </StyledAvatarWrapper>
    );
  }

  const isChatUserOnlineIndication = isChatAvatar && isChatUserOnline && !author;

  return (
    <TooltipCoordinator placement="bottom" render={isChatUserOnlineIndication && (() => onlineLabel)}>
      <PopoverCoordinator
        renderButton={() => (
          <StyledAvatarWrapper className={props.className}>
            <StyledAvatarDiv className={buildHoverParentClassName()}>
              {renderAvatar(props, theme)}
            </StyledAvatarDiv>
          </StyledAvatarWrapper>
        )}
        renderModal={(popoverProps) => renderContactDropdown(popoverProps, panelId, author)}
      />
    </TooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function renderAvatar(props: MessageViewerAvatarProps, theme: DefaultTheme) {
  // If this is from a teammate, render the teammate avatar.
  const {author, isChatAvatar, isChatUserOnline} = props;

  if (!isUndefined(author) && !isVisualRecipient(author)) {
    return <TeammateAvatar supportsHover hasDarkerHover teammate={author} size={avatarSize} />;
  }

  if (!author) {
    const error = new Error(`A message with no "from" or "author" was provided (${props.message.id})`);
    reportToBugsnag(error);

    return <UnknownAvatar size={avatarSize} />;
  }

  const isChatUserOnlineIndication = isChatAvatar && isChatUserOnline && isVisualRecipient(author);
  return (
    <ContactAvatar visualContact={author} size={avatarSize} supportsHover hasDarkerHover>
      {isChatUserOnlineIndication && (
        <AvatarBadgeStatus
          borderColor={theme.appBackground}
          size={avatarSize}
          onlineStatus={TeammateOnlineStatusesEnum.ONLINE}
          label={onlineLabel}
        />
      )}
    </ContactAvatar>
  );
}

function renderContactDropdown(
  popoverProps: PopoverTransferProps,
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | AccountDetailsPanelId | ContactDetailsPanelId,
  fromRecipient: VisualRecipientModel,
) {
  return (
    <ContactCardWrapperContainer
      visualContact={fromRecipient}
      panelId={panelId}
      popoverProps={popoverProps}
      location={ContactCardLocationsEnum.MESSAGE_VIEWER}
    />
  );
}
